// AvatarImage
// ***********************************************************************************************************************
import CloseIcon from "@mui/icons-material/Close";
import { Avatar, Box, IconButton, Tooltip, useTheme } from "@mui/material";
import * as React from "react";
import { useRef, useState } from "react";
import { Base } from "../../../../framework/base";
import { Translations } from "../../../../models/translations";

const avatarColors = [
    "#bfc9ca",
    "#1abc9c",
    "#e8f8f5",
    "#d1f2eb",
    "#a3e4d7",
    "#76d7c4",
    "#48c9b0",
    "#16a085",
    "#e8f6f3",
    "#d0ece7",
    "#a2d9ce",
    "#73c6b6",
    "#45b39d",
    "#2ecc71",
    "#eafaf1",
    "#d5f5e3",
    "#abebc6",
    "#82e0aa",
    "#58d68d",
    "#27ae60",
    "#e9f7ef",
    "#d4efdf",
    "#a9dfbf",
    "#7dcea0",
    "#52be80",
    "#3498db",
    "#ebf5fb",
    "#d6eaf8",
    "#aed6f1",
    "#85c1e9",
    "#5dade2",
    "#2980b9",
    "#eaf2f8",
    "#d4e6f1",
    "#a9cce3",
    "#7fb3d5",
    "#5499c7",
    "#9b59b6",
    "#f5eef8",
    "#ebdef0",
    "#d7bde2",
    "#c39bd3",
    "#af7ac5",
    "#8e44ad",
    "#f4ecf7",
    "#e8daef",
    "#d2b4de",
    "#bb8fce",
    "#a569bd",
    "#ebedef",
    "#d6dbdf",
    "#aeb6bf",
    "#85929e",
    "#eaecee",
    "#d5d8dc",
    "#abb2b9",
    "#808b96",
    "#f1c40f",
    "#fef9e7",
    "#fcf3cf",
    "#f9e79f",
    "#f7dc6f",
    "#f4d03f",
    "#f39c12",
    "#fef5e7",
    "#fdebd0",
    "#fad7a0",
    "#f8c471",
    "#f5b041",
    "#e67e22",
    "#fdf2e9",
    "#fae5d3",
    "#f5cba7",
    "#f0b27a",
    "#eb984e",
    "#d35400",
    "#fbeee6",
    "#f6ddcc",
    "#edbb99",
    "#e59866",
    "#dc7633",
    "#e74c3c",
    "#fdedec",
    "#fadbd8",
    "#f5b7b1",
    "#f1948a",
    "#ec7063",
    "#c0392b",
    "#f9ebea",
    "#f2d7d5",
    "#e6b0aa",
    "#d98880",
    "#cd6155",
    "#ecf0f1",
    "#fdfefe",
    "#fbfcfc",
    "#f7f9f9",
    "#f4f6f7",
    "#f0f3f4",
    "#bdc3c7",
    "#f8f9f9",
    "#f2f3f4",
    "#e5e7e9",
    "#d7dbdd",
    "#cacfd2",
    "#95a5a6",
    "#f4f6f6",
    "#eaeded",
    "#d5dbdb",
    //"#bfc9ca",
    "#aab7b8",
    "#7f8c8d",
    "#f2f4f4",
    "#e5e8e8",
    "#ccd1d1",
];

export interface IAvatarImageProp {
  id?: string;
  border?: boolean;
  name?: string;
  icon?: React.ReactNode;
  image?: string;
  editable?: boolean;
  fileInputName?: string;
  tooltip?: string;
  removeTooltip?: string;
  longPressMs?: number;
  size?: number;
  logo?: boolean;
  marginRight?: number | string;
  marginLeft?: number | string;
  selected?: boolean;
  objectFit?: "contain" | "cover" | "fill" | "none" | "scale-down";
  onClick?: () => void;
  onChange?: (file: File) => void;
  onClear?: () => void;
  onLongPress?: () => void;
  onError?: (message: string) => void;
}

export interface IAvatarImageState {
  file: File;
  backgroundNumber: number;
}

export const AvatarImage = ({
    id,
    border,
    name,
    icon,
    image,
    editable,
    fileInputName,
    tooltip,
    removeTooltip,
    longPressMs,
    size,
    logo,
    marginRight,
    marginLeft,
    selected,
    objectFit,
    onClick,
    onChange,
    onClear,
    onLongPress,
    onError,
}: IAvatarImageProp) => {
    const pressTimer = useRef(0);
    const theme = useTheme();
    const inputRef = useRef<HTMLInputElement>(null);

    const [state, setState] = useState<IAvatarImageState>({
        file: null,
        backgroundNumber: Base.strToHashNumber(id, 114),
    });

    const clearPressTimer = () => {
        clearTimeout(pressTimer.current);
        pressTimer.current = 0;
    };

    const handleLinePress = (ev) => {
    //Set timer
        pressTimer.current = window.setTimeout(() => {
            //long press
            clearPressTimer();
            onLongPress();
        }, longPressMs);
    };

    const handleLineRelease = (ev) => {
    //short press
        if (pressTimer.current > 0) {
            clearPressTimer();
            if (onClick) {
                onClick();
            }
        }
    };

    React.useEffect(() => {
        setState((old) => ({
            ...old,
            backgroundNumber: Base.strToHashNumber(image || icon ? "" : id, 114),
        }));
    }, [id, image]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (
            !editable ||
      Base.isNullOrUndefined(e.target.files) ||
      e.target.files.length < 1
        ) {
            return;
        }
        const file = e.target.files[0];
        if (Base.isImageFile(file)) {
            onChange(file);
        } else {
            onError(Translations.SelectImageFile);
        }
    };

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (onClick) {
            e.stopPropagation();
            onClick();
        }

        if (editable) {
            inputRef.current.click();
        }
    };

    let displayName = name ? name.trim() : "";
    if (displayName.length > 2 && displayName[0] === "'" && displayName[displayName.length - 1] === "'") {
        displayName = displayName.substring(1, displayName.length - 2);
    }
    const names = displayName.split(" ");
    const avatarColor =
    avatarColors[(state.backgroundNumber - 1).toString(10)] || (logo ? "rgba(0, 0, 0, 0)" : theme.palette.grey[200]);
    const fontSize = size > 59 ? "1.25rem" : size > 40 ? ".75rem" : size >= 30 ? ".9rem" : ".6875rem";

    const avatarElement =
        <Avatar
            onClick={handleClick}
            onTouchStart={onLongPress && editable ? handleLinePress : null}
            onTouchEnd={onLongPress && editable ? handleLineRelease : null}
            onMouseDown={onLongPress && editable ? handleLinePress : null}
            onMouseUp={onLongPress && editable ? handleLineRelease : null}
            onMouseLeave={onLongPress && editable ? handleLineRelease : null}
            src={image}
            sx={{
                "width": size || 24,
                "height": size || 24,
                fontSize,
                "backgroundColor": avatarColor,
                "color": theme.palette.getContrastText(avatarColor),
                "cursor": editable ? "pointer" : "inherit",
                "border": border ? "1px solid" : "none",
                "borderColor": "divider",
                "minWidth": logo ? "270px" : undefined,
                "borderRadius": logo ? "0" : undefined,
                "boxShadow": selected ? `0 0 0 2px ${theme.palette.primary[200]}` : undefined,
                ".MuiAvatar-img": {
                    objectFit,
                },
            }}
        >
            {icon ?? (displayName
                ? names[0]
                    ? names[0][0] + (names[1] ? names[1][0] : "")
                    : ""
                : "?"
            ).toUpperCase()}
        </Avatar>;

    return (
        <Tooltip title={!editable ? tooltip : null}>
            <Box
                sx={{
                    position: "relative",
                    minWidth: logo ? "270px" : undefined,
                    marginLeft,
                    marginRight,
                }}
            >
                {(editable && tooltip
                    ? <Tooltip title={tooltip}>{avatarElement}</Tooltip>
                    : avatarElement
                )}
                {!!editable && !!image && !!onClear
                    ? (
                        <Tooltip title={removeTooltip}>
                            <Box position="absolute" sx={{ right: "-1rem", top: "-1rem" }}>
                                <IconButton
                                    size="small"
                                    onClick={
                                        onClear
                                            ? (e) => {
                                                e.stopPropagation();
                                                onClear();
                                            }
                                            : null
                                    }
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </Tooltip>
                    )
                    : null}
                {!!editable && (
                    <input
                        ref={inputRef}
                        type="file"
                        name={fileInputName || "image"}
                        style={{ display: "none" }}
                        multiple={false}
                        onChange={handleFileChange}
                    />
                )}
            </Box>
        </Tooltip>
    );
};
